<script setup lang="ts">
import type { SidebarProps } from "@/components/ui/sidebar";
import { type RouteLocationRaw, useRoute, useRouter } from "vue-router";
import {
  LayoutDashboard,
  GraduationCap,
  MessageSquareText,
  Headset,
  ChartLine,
  ChartPie,
  Workflow,
  Folder,
  Form,
  BookOpenText,
  SlidersVertical,
  Cable,
  LucideCog,
  Split,
  Bot,
  Wrench,
  ShoppingBasket,
  Mail,
  TrendingUp,
  DiamondPlus,
  CircleUser,
  LogOut,
  Lightbulb,
  FileText,
  Users,
} from "@lucide/vue";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { type Component, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import { useBotsStore } from "@/stores/bots";
import BotPicker from "@/components/BotPicker.vue";
import { isRouteActive } from "@/router/router.ts";
import { TrackingService } from "@/services/tracking";
import { useLeadRestriction } from "@/components/leadBot/leadRestriction";

const authStore = useAuthStore();
const botsStore = useBotsStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const props = defineProps<SidebarProps>();
const { isLocked, isRestricted } = useLeadRestriction();
/** Locked for a lead: still clickable, as it opens the demo request, but dimmed */
const isLockedForLead = (to: RouteLocationRaw) => isLocked(router.resolve(to));
/** Never a locked entry, e.g. a lead's bot settings while on its users & permissions page, which is below it */
const isActiveEntry = (to: RouteLocationRaw) => isRouteActive.value(to) && !isLockedForLead(to);
const isSimpleEdit = computed(() => authStore.isSimpleEdit);
const isAdmin = computed(() => authStore.isAdmin);
const isOwner = computed(() => authStore.isOwner);
const isLivechat = computed(() => authStore.isLivechat);
const isConversationalView = computed(() => authStore.isConversationalView);
const isFormView = computed(() => authStore.isFormView);
const isRAG = computed(() => authStore.isRAG);
const isWidgetEditorView = computed(() => authStore.isWidgetEditorView);
const getBotSettings = computed(() => botsStore.getBotSettings);
const currentBotId = computed(() => botsStore.currentBotId);
const hasWidget = computed(() => botsStore.hasWidget);

const isLivechatAvailable = computed<boolean>(
  () => isLivechat.value && getBotSettings.value(currentBotId.value)?.moin_livechat
);
const isLivechatStatisticsDisabled = computed<boolean>(
  () => getBotSettings.value(currentBotId.value)?.moin_livechat?.disableStatistics
);

const logout = async () => {
  await botsStore.clear();
  await authStore.logout({
    reload: true,
    byUser: true,
  });
};

/**
 * Menu click tracking, carried over from the legacy HubMenu (`source: 'Menu'`).
 * A click on the page you are already on is not a navigation, so it is skipped
 * like it was there.
 */
function trackMenuClick(to: RouteLocationRaw) {
  const routerPath = router.resolve(to).fullPath;
  if (routerPath === route.fullPath) return;

  TrackingService.trackMenu({
    botId: currentBotId.value ?? undefined,
    type: "click",
    source: "Menu",
    payload: { routerPath },
  });
}

interface SidebarSection {
  title: string | null;
  items: Array<{
    /** Lucide icon component */
    icon: Component;
    title: string;
    to: RouteLocationRaw;
  }>;
}
const sidebarSections = computed<SidebarSection[]>(() => {
  const sections: SidebarSection[] = [
    {
      title: null,
      items: [
        {
          icon: LayoutDashboard,
          title: t("menu.dashboard"),
          to: { name: "welcome" },
        },
      ],
    },
  ];

  const liveArea: SidebarSection = {
    title: t("menu.areaLive"),
    items: [
      {
        title: t("menu.conversations"),
        icon: MessageSquareText,
        // Admins land on the new Conversation Insights dashboard
        // (/insights/dashboard, admin-only). Everyone else keeps the
        // existing conversations view.
        to: isAdmin.value ? { name: "insights-landing" } : { name: "chats" },
      },
      {
        title: t("livechat.title"),
        icon: Headset,
        to: { name: "livechat" },
      },
      {
        title: t("stats.title"),
        icon: ChartLine,
        to: { name: "statistics" },
      },
    ],
  };
  sections.push(liveArea);
  if (isSimpleEdit.value) {
    liveArea.items.unshift({
      icon: GraduationCap,
      title: t("ai.title"),
      to: { name: "ai" },
    });
  }
  if (
    isLivechatAvailable.value &&
    (isAdmin.value || isOwner.value) &&
    !isLivechatStatisticsDisabled.value
  ) {
    liveArea.items.push({
      title: t("settingsLivechat.livechatStats.title"),
      icon: ChartPie,
      to: { name: "livechat-statistics" },
    });
  }
  if (isSimpleEdit.value) {
    liveArea.items.push({
      title: t("dreaming.title"),
      icon: Lightbulb,
      to: { name: "dreaming" },
    });
  }

  if (isSimpleEdit.value) {
    const editorArea: SidebarSection = {
      title: t("menu.areaEditor"),
      items: [
        {
          title: t("menu.answers"),
          icon: Folder,
          to: { name: "intents" },
        },
      ],
    };
    sections.push(editorArea);

    if (isConversationalView.value) {
      editorArea.items.unshift({
        title: t("menu.conversational"),
        icon: Workflow,
        to: { name: "conversational" },
      });
    }

    if (isFormView.value) {
      editorArea.items.push({
        title: t("menu.forms"),
        icon: Form,
        to: { name: "Forms" },
      });
    }
    if (isRAG.value) {
      // Bots still on a legacy LLM model can't use the Knowledge Base
      if (!botsStore.hasLLMLegacyModel) {
        editorArea.items.push({
          title: t("menu.knowledgeBase"),
          icon: BookOpenText,
          to: { name: "knowledge-base" },
        });
      }
      // Product Advisor is hidden from the menu, as on dev (a00f1de1e). The route stays reachable.
    }
    if (isWidgetEditorView.value && hasWidget.value) {
      editorArea.items.push({
        title: t("menu.widgetAndTeaser"),
        icon: LucideCog,
        to: { name: "wt" },
      });
    }
  }

  const areaSetup: SidebarSection = {
    title: t("menu.areaSetup"),
    items: [],
  };
  // Mirrors the legacy Hub menu rule: visible to simple-edit users who are admin OR owner.
  if (isSimpleEdit.value && (isAdmin.value || isOwner.value)) {
    areaSetup.items.push({
      title: t("menu.settings"),
      icon: SlidersVertical,
      to: { name: "settings" },
    });
  }
  // A lead may invite colleagues, but the way there, the bot settings, is locked for them
  if (isRestricted.value) {
    areaSetup.items.push({
      title: t("botSettings.roles.title"),
      icon: Users,
      to: { name: "settings-roles" },
    });
  }
  if (isAdmin.value || isOwner.value) {
    areaSetup.items.push({
      title: t("menu.integrations"),
      icon: Cable,
      to: { name: "integrations" },
    });
  }
  if (areaSetup.items.length) sections.push(areaSetup);

  return sections;
});

const adminSections = computed<SidebarSection[]>(() => {
  if (!isAdmin.value) return [];

  return [
    {
      title: "Admin",
      items: [
        {
          title: t("menu.channels"),
          icon: Split,
          to: { name: "channels" },
        },
        {
          title: t("menu.botmanager"),
          icon: Bot,
          to: { name: "bots" },
        },
        {
          title: t("menu.tools"),
          icon: Wrench,
          to: { name: "tools" },
        },
        {
          title: t("lizenz"),
          icon: ShoppingBasket,
          to: { name: "licence" },
        },
        {
          title: "Bot Settings",
          icon: LucideCog,
          to: { name: "bot-settings" },
        },
        {
          title: "Bot Notifications",
          icon: Mail,
          to: { name: "notifications" },
        },
        {
          title: "QM Dashboard",
          icon: TrendingUp,
          to: { name: "admin-qm" },
        },
        {
          title: "Demo Creation",
          icon: DiamondPlus,
          to: { name: "demo-creation" },
        },
        {
          title: "Prompt Manager",
          icon: FileText,
          to: { name: "prompt-manager" },
        },
      ],
    },
  ];
});
</script>

<template>
  <Sidebar v-bind="props">
    <SidebarHeader>
      <div style="height: 64px; padding-top: 4px" class="mx-auto">
        <img src="@/assets/moin-logo.svg" height="50px" alt="moinAI Hub Logo" />
      </div>
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup
        v-for="(section, i) in sidebarSections"
        :key="i"
        class="border-b border-gray-200"
      >
        <SidebarGroupLabel v-if="section.title">
          {{ section.title }}
        </SidebarGroupLabel>

        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem
              v-for="childItem in section.items"
              :key="childItem.title"
              :class="{ 'opacity-50': isLockedForLead(childItem.to) }"
            >
              <SidebarMenuButton
                as-child
                :is-active="isActiveEntry(childItem.to)"
              >
                <RouterLink :to="childItem.to" @click="trackMenuClick(childItem.to)">
                  <component
                    :is="childItem.icon"
                    class="inline"
                    :class="{ 'text-success-600': !isActiveEntry(childItem.to) }"
                  />

                  <span>{{ childItem.title }}</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <!-- Spacer pins the admin box and profile group to the bottom -->
      <div class="mt-auto" />

      <div v-if="isAdmin" class="mx-2 mb-2 border border-admin rounded-lg">
        <SidebarGroup v-for="(section, i) in adminSections" :key="i">
          <SidebarGroupLabel v-if="section.title">
            {{ section.title }}
          </SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem
                v-for="childItem in section.items"
                :key="childItem.title"
              >
                <SidebarMenuButton
                  as-child
                  :is-active="isRouteActive(childItem.to)"
                >
                  <RouterLink :to="childItem.to" @click="trackMenuClick(childItem.to)">
                    <component :is="childItem.icon" class="inline" style="color: #0eab9a" />

                    <span>{{ childItem.title }}</span>
                  </RouterLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div class="m-2 relative">
          <BotPicker />
        </div>
      </div>

      <SidebarGroup>
        <SidebarGroupLabel>{{ t("menu.profile") }}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                as-child
                :is-active="isRouteActive({ name: 'user-profile' })"
              >
                <RouterLink :to="{ name: 'user-profile' }" @click="trackMenuClick({ name: 'user-profile' })">
                  <CircleUser />
                  <span>{{ t("menu.myProfile") }}</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton @click="logout()">
                <LogOut />
                <span>{{ t("menu.logout") }}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>

    <!-- Gray divider that is interactable -->
    <!-- Click toggles the sidebar, drag resizes it -->
    <SidebarRail :label="t('menu.sidebarRail')" />
  </Sidebar>
</template>
